import { SUBSTATUS } from "Services/Status/Substatus.types";
import { NOTIFICATIONTYPE } from "Store/Notification/ActionTypes";
import { StoreState } from "Store/Reducers";
import { ACTION_TYPE, getActionTypes } from "Store/Shared/Actions/Helpers";
import { API_URL } from "Store/Shared/Middleware/API/Constants";
import ACTIONS, { BUSINESS_UNIT, FETCH_ALL_BUSINESS_UNIT, FETCH_BUSINESS_UNIT, FETCH_BUSINESS_UNIT_DATASET, SAVE_BUSINESS_UNIT, SAVE_BUSINESS_UNIT_STATUS } from "./ActionTypes";
import { BusinessUnitId } from "./PropTypes";
import {fetchContactV2} from 'Store/Contact/Actions';

export const fetchBusinessUnitOverviewByPagenumber = (
    overview_fields: string[],
    pageNumber: number = 1,
    filters: any = false,
    forceFetch: boolean = false
): ACTION_TYPE => {
    let flattenFilters = '';
    const filterKeys = Object.keys(filters);
    if (filterKeys) {
        for (const filterKey of filterKeys) {
            let values = filters[filterKey];
            if (Array.isArray(filters[filterKey])) {
                values = values.join(',');
            }
            flattenFilters += `&filter[${filterKey}]=${values}`;
        }
    }

    return {
        types: getActionTypes(ACTIONS.FETCH_BUSINESS_UNITS_OVERVIEW_PAGE),
        shouldCallAPI: ({ business_units }) => forceFetch || !business_units._isFetchingPage.includes(pageNumber),
        callAPI: {
            url: `/api/v2/overview?entity=business_unit&page=${pageNumber}&overview_fields=${overview_fields ?? ''}${flattenFilters}`
        },
        payload: {
            page_number: pageNumber,
            isFiltering: flattenFilters.length > 0
        }
    }
}

export const createBusinessUnit = (data: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.CREATE_BUSINESS_UNIT),
    callAPI: {
        method: 'POST',
        url: '/api/v2/business_units',
        body: data
    }
});

export const createBusinessUnitValues = (id: BusinessUnitId, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.CREATE_BUSINESS_UNIT_VALUES),
    callAPI: {
        method: 'POST',
        url: `/api/v2/business_units/${id}/values`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    },
    callback: fetchBusinessUnitV2(id)
})

export const backgroundCreateBusinessUnitValues = (id: BusinessUnitId, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.BACKGROUND_CREATE_BUSINESS_UNIT_VALUES),
    callAPI: {
        method: 'POST',
        url: `/api/v2/business_units/${id}/values`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    },
})

export const fetchAllBusinessUnits = (): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.FETCH_ALL_BUSINESS_UNITS),
    shouldCallAPI: state => state.business_units._forceFetch,
    callAPI: {
        url: '/api/v2/business_units'
    }
});

export const fetchBusinessUnitV2 = (id: BusinessUnitId): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.FETCH_BUSINESS_UNIT),
    callAPI: {
        url: `/api/v2/business_units/${id}`
    },
    parralel: fetchBusinessUnitValues(id)
});

export const fetchBusinessUnitValues = (id: BusinessUnitId): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.FETCH_BUSINESS_UNIT_VALUES),
    shouldCallAPI: state => state.business_units._forceFetch,
    callAPI: {
        url: `/api/v2/business_units/${id}/values`
    }
});

export const fetchBusinessUnitConcept = (): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.FETCH_BUSINESS_UNIT_CONCEPT),
    shouldCallAPI: state => state.business_units._forceFetch,
    callAPI: { url: `/api/v2/business_units?substatus=${SUBSTATUS.ACTIVE.RECORD_CONCEPT}` }
});

export const updateBusinessUnitValues = (id: BusinessUnitId, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.UPDATE_BUSINESS_UNIT_VALUES),
    callAPI: {
        method: 'PATCH',
        url: `/api/v2/business_units/${id}/values`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    },
    callback: fetchBusinessUnitV2(id)
})

export const upsertBusinessUnitValues = (id: any, data: any) => ({
    types: getActionTypes(ACTIONS.UPSERT_BUSINESS_UNITS_VALUES),
    callAPI: {
        method: 'PUT',
        url: `/api/v2/business_units/${id}/values`,
        body: {
            values: data
        }
    }
})

export const backgroundUpdateBusinessUnitValues = (id: BusinessUnitId, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.BACKGROUND_UPDATE_BUSINESS_UNIT_VALUES),
    callAPI: {
        method: 'PATCH',
        url: `/api/v2/business_units/${id}/values`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    }
})

export const updateBusinessUnit = (id: any, body: any): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.UPDATE_BUSINESS_UNIT),
    callAPI: {
        method: 'PATCH',
        url: `/api/v2/business_units/${id}`,
        body: body
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol aangepast',
        [NOTIFICATIONTYPE.ERROR]: 'Aanpassing mislukt'
    },
    callback: {
        types: [
            FETCH_BUSINESS_UNIT.REQUEST,
            FETCH_BUSINESS_UNIT.SUCCESS,
            FETCH_BUSINESS_UNIT.FAILURE
        ],
        callAPI: {
            url: `/api/business_units/${id}`
        }
    }
})

export const deleteBusinessUnit = (id: BusinessUnitId): ACTION_TYPE => ({
    types: getActionTypes(ACTIONS.DELETE_BUSINESS_UNIT),
    callAPI: {
        method: 'DELETE',
        url: `/api/v2/business_units/${id}`
    },
    notification: {
        [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol verwijderd',
        [NOTIFICATIONTYPE.ERROR]: 'Verwijderen mislukt'
    },
});


export function fetchAllBusinessUnit(page, searchFilter, overviewFilter) {
    let urlParams = '';
    if (searchFilter) {
        urlParams += `&searchFilter=${searchFilter}`
    }

    Object.keys(overviewFilter)?.map((i) => {
        urlParams += `&${encodeURIComponent(i)}=${overviewFilter[i]}`;
    });
    
    return {
        types: [
            FETCH_ALL_BUSINESS_UNIT.REQUEST,
            FETCH_ALL_BUSINESS_UNIT.SUCCESS,
            FETCH_ALL_BUSINESS_UNIT.FAILURE
        ],
        shouldCallAPI: (state: any) => true,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/overview?entity=business_unit&page=${page}${urlParams}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
    }
}

export function fetchBusinessUnit(id: string) {
    return {
        types: [
            FETCH_BUSINESS_UNIT.REQUEST,
            FETCH_BUSINESS_UNIT.SUCCESS,
            FETCH_BUSINESS_UNIT.FAILURE
        ],
        shouldCallAPI: (state: any) => true,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/business_units/${id}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
    }
}

export function saveBusinessUnit(candidateId: any, data: any) {
    return {
        types: [
            SAVE_BUSINESS_UNIT.REQUEST,
            SAVE_BUSINESS_UNIT.SUCCESS,
            SAVE_BUSINESS_UNIT.FAILURE
        ],
        notification: {
            [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol opgeslagen',
            [NOTIFICATIONTYPE.ERROR]: 'Opslaan mislukt'
        },
        shouldCallAPI: () => true,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/business_units/${candidateId}`,
            {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify(data)
            }),
        callback: fetchBusinessUnitValues(candidateId)
    }
}


export function saveBusinessUnitEntity(candidateId: any, data: any) {
    return {
        types: [
            SAVE_BUSINESS_UNIT_STATUS.REQUEST,
            SAVE_BUSINESS_UNIT_STATUS.SUCCESS,
            SAVE_BUSINESS_UNIT_STATUS.FAILURE
        ],
        notification: {
            [NOTIFICATIONTYPE.SUCCESS]: 'Succesvol opgeslagen',
            [NOTIFICATIONTYPE.ERROR]: 'Opslaan mislukt'
        },
        shouldCallAPI: () => true,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/business_units/${candidateId}`,
            {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify(data)
            })
    }
}

export function editBusinessUnit(candidateId: any, fieldId: any, valueId: any, value: any) {
    return {
        type: BUSINESS_UNIT.EDIT,
        payload: {
            candidateId: candidateId,
            fieldId: fieldId,
            valueId: valueId,
            value: value
        }
    }
}

export function resetBusinessUnit(candidateId: any, fieldIds: any) {
    return {
        type: BUSINESS_UNIT.RESET,
        payload: {
            candidateId: candidateId,
            fieldIds: fieldIds
        }
    }
}

export function fetchBusinessUnitDataset() {
    return {
        types: [
            FETCH_BUSINESS_UNIT_DATASET.REQUEST,
            FETCH_BUSINESS_UNIT_DATASET.SUCCESS,
            FETCH_BUSINESS_UNIT_DATASET.FAILURE
        ],
        shouldCallAPI: ({business_units}: StoreState) => business_units.datasets._forceFetch,
        callAPI: (accessToken: any) => fetch(
            API_URL + `/api/business_units/dataset`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
    }
}